import { motion, AnimatePresence } from 'framer-motion';
import { CardBrandIcon } from './CardBrandIcon.jsx';
import { detectBrand, digitsOnly, BRAND_LABELS } from './cardUtils.js';

/**
 * A live card that fills in as the customer types, and flips when they reach the
 * security code.
 *
 * The flip is not decoration. The security code is in a different place on every
 * network — back for Visa and Mastercard, front for American Express — and
 * turning the card over at the moment the field is focused answers "where do I
 * look?" without a line of instructional copy. The rest of the card exists so
 * there is somewhere for the digits to *go*: typing into a bare input gives no
 * feedback that anything was understood, and a mistyped brand is obvious the
 * instant the wrong logo appears.
 */
const EASE = [0.2, 0.7, 0.2, 1];

/** Deep, desaturated grounds — the card should read as a card, not as a banner. */
const GROUNDS = {
  visa: 'linear-gradient(135deg, #1a2a6c 0%, #12203f 55%, #0b1428 100%)',
  mastercard: 'linear-gradient(135deg, #2b2118 0%, #1c1713 55%, #121010 100%)',
  amex: 'linear-gradient(135deg, #0f4f52 0%, #0b3538 55%, #072426 100%)',
  discover: 'linear-gradient(135deg, #4a2c14 0%, #2e1d10 55%, #1b120a 100%)',
  jcb: 'linear-gradient(135deg, #1f3a5f 0%, #16283f 55%, #0e1a2a 100%)',
  diners: 'linear-gradient(135deg, #123a52 0%, #0d2739 55%, #081a27 100%)',
  default: 'linear-gradient(135deg, #2a2a2e 0%, #1c1c1f 55%, #141416 100%)',
};

/** Groups the typed digits into the brand's own print pattern, padding the rest. */
function displayGroups(value, brand) {
  const digits = digitsOnly(value);
  const pattern = brand === 'amex' ? [4, 6, 5] : [4, 4, 4, 4];

  const groups = [];
  let cursor = 0;
  for (const size of pattern) {
    const slice = digits.slice(cursor, cursor + size);
    groups.push(slice.padEnd(size, '•'));
    cursor += size;
  }
  return groups;
}

function Digit({ char, index }) {
  const filled = char !== '•';
  return (
    <span className="relative inline-block" style={{ width: '0.62em' }}>
      <AnimatePresence initial={false} mode="popLayout">
        <motion.span
          key={`${index}-${char}`}
          initial={{ opacity: 0, y: filled ? 6 : 0, scale: filled ? 0.85 : 1 }}
          animate={{ opacity: filled ? 1 : 0.32, y: 0, scale: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.16, ease: EASE }}
          className="absolute inset-0 text-center"
        >
          {char}
        </motion.span>
      </AnimatePresence>
    </span>
  );
}

export function CardPreview({ number, expiry, cvc, holder, flipped = false, focused }) {
  const brand = detectBrand(number);
  const ground = GROUNDS[brand.brand] ?? GROUNDS.default;
  const groups = displayGroups(number, brand.brand);
  const isAmex = brand.brand === 'amex';

  return (
    <div className="[perspective:1400px]">
      <motion.div
        className="relative w-full"
        style={{ aspectRatio: '1.586 / 1', transformStyle: 'preserve-3d' }}
        animate={{ rotateY: flipped && !isAmex ? 180 : 0 }}
        transition={{ duration: 0.55, ease: EASE }}
      >
        {/* ------------------------------- front ------------------------------- */}
        <div
          className="absolute inset-0 overflow-hidden rounded-2xl p-5 text-white shadow-lg"
          style={{ background: ground, backfaceVisibility: 'hidden' }}
        >
          {/* A single soft sheen — enough to read as a physical surface. */}
          <div
            className="pointer-events-none absolute inset-0"
            style={{
              background:
                'radial-gradient(120% 80% at 85% 0%, rgba(255,255,255,0.14), transparent 55%),' +
                'radial-gradient(80% 60% at 0% 100%, rgba(255,255,255,0.07), transparent 60%)',
            }}
          />

          <div className="relative flex h-full flex-col justify-between">
            <div className="flex items-start justify-between">
              {/* The chip — drawn, so it scales and never 404s. */}
              <svg viewBox="0 0 34 26" className="h-[26px] w-[34px]" aria-hidden="true">
                <rect x="0.5" y="0.5" width="33" height="25" rx="4" fill="#d9c07a" opacity="0.9" />
                <path d="M11 0v26M23 0v26M0 8.5h34M0 17.5h34" stroke="#8d7a45" strokeWidth="0.9" opacity="0.7" />
              </svg>

              {/*
                * initial={false} so the mark is painted at its final state on
                * first render and only *changes* animate. Depending on an entry
                * animation would leave the logo invisible if the first frame
                * never runs.
                */}
              <AnimatePresence mode="wait" initial={false}>
                <motion.div
                  key={brand.brand ?? 'generic'}
                  initial={{ opacity: 0, scale: 0.9, y: -4 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.9, y: 4 }}
                  transition={{ duration: 0.22, ease: EASE }}
                >
                  <CardBrandIcon
                    brand={brand.brand}
                    variant="mono"
                    className={`h-[26px] w-12 ${brand.brand ? 'text-white' : 'text-white/40'}`}
                  />
                </motion.div>
              </AnimatePresence>
            </div>

            <div
              className={`font-mono tabular tracking-[0.06em] text-white transition-opacity ${
                focused === 'number' ? 'opacity-100' : 'opacity-95'
              }`}
              style={{ fontSize: 'clamp(15px, 4.6vw, 20px)' }}
              aria-hidden="true"
            >
              {groups.map((group, gi) => (
                <span key={gi} className={gi > 0 ? 'ml-[0.5em]' : ''}>
                  {group.split('').map((char, ci) => (
                    <Digit key={`${gi}-${ci}`} char={char} index={`${gi}-${ci}`} />
                  ))}
                </span>
              ))}
            </div>

            <div className="flex items-end justify-between gap-4">
              <div className="min-w-0">
                <p className="text-[8.5px] uppercase tracking-[0.14em] text-white/45">Card holder</p>
                <p className="truncate text-[12px] font-medium uppercase tracking-wide text-white/90">
                  {holder?.trim() || 'YOUR NAME'}
                </p>
              </div>
              <div className="shrink-0 text-right">
                <p className="text-[8.5px] uppercase tracking-[0.14em] text-white/45">Valid thru</p>
                <p className="tabular text-[12px] font-medium text-white/90">
                  {expiry?.replace(/\s/g, '') || 'MM/YY'}
                </p>
              </div>
              {/* Amex prints its code on the front, so it belongs here. */}
              {isAmex && (
                <div className="shrink-0 text-right">
                  <p className="text-[8.5px] uppercase tracking-[0.14em] text-white/45">CID</p>
                  <p className="tabular text-[12px] font-medium text-white/90">{cvc || '••••'}</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* -------------------------------- back -------------------------------- */}
        <div
          className="absolute inset-0 overflow-hidden rounded-2xl text-white shadow-lg"
          style={{ background: ground, backfaceVisibility: 'hidden', transform: 'rotateY(180deg)' }}
        >
          <div className="mt-6 h-11 w-full bg-black/70" />
          <div className="px-5 pt-5">
            <div className="flex items-center gap-3">
              <div className="h-8 flex-1 rounded bg-white/85" />
              <div className="grid h-8 min-w-[58px] place-items-center rounded bg-white px-2">
                <span className="tabular font-mono text-[13px] font-semibold text-[#1c1c1f]">
                  {cvc || '•••'}
                </span>
              </div>
            </div>
            <p className="mt-3 text-[9px] leading-relaxed text-white/45">
              The {BRAND_LABELS[brand.brand] ?? 'security'} code is the {brand.cvcLength} digits printed here.
            </p>
          </div>
        </div>
      </motion.div>
    </div>
  );
}

export default CardPreview;
